import { User } from 'modules/graphql';
import { AVATAR_SIZES, Avatar } from 'ui/avatar';
import { TipPlacement } from 'ui/tooltip';
import { Logo } from 'ui/logo';
import { displayDateWithTodayAndYesterdayPrefix } from 'utils/date';
import { DISPLAY_CONTENT, FREQUENCY_LABELS } from '../constants';
import { Frequency, LastModified, OptOutType } from '../types';
import { addLinkStyling, getCharacterCountText, getDateTime, getFlagIcon } from '../utils';
import {
  FrequencyCellWrapper,
  InstructionsCellWrapper,
  LastModifiedCellWrapper,
  LastModifiedDatesWrapper,
  RegionCellWrapper,
  StyledTooltip,
} from './styles';

type RegionCellProps = {
  regionName: string;
  regionCode: string;
};

export const RegionCell = ({ regionName, regionCode }: RegionCellProps) => (
  <RegionCellWrapper data-test={`region-cell-${regionCode.toLowerCase()}`}>
    {getFlagIcon(regionCode)}
    {regionName}
  </RegionCellWrapper>
);

type InstructionsCellProps = {
  regionName: string;
  instructions: string | null;
  type: OptOutType;
};

export const InstructionsCell = ({ regionName, instructions, type }: InstructionsCellProps) => {
  const isInstructionsNotAvailable = !instructions;
  const isAlphanumeric = type === OptOutType.ALPHANUMERIC;
  const isNumeric = type === OptOutType.NUMERIC;

  const buildTooltipContent = () => {
    if (!isAlphanumeric && !isNumeric) return '';

    return `${type.charAt(0).toUpperCase() + type.slice(1)} ${
      DISPLAY_CONTENT.contentTable.instructionsTooltipLabel
    } ${regionName}`;
  };

  return (
    <InstructionsCellWrapper
      data-test="instructions-cell"
      displayAsSecondaryText={isInstructionsNotAvailable}
    >
      <StyledTooltip
        isEnabled={isInstructionsNotAvailable}
        placement={TipPlacement.Right}
        tooltipContent={buildTooltipContent()}
      >
        {addLinkStyling(instructions) || 'N/A'}
      </StyledTooltip>
      {!isInstructionsNotAvailable && getCharacterCountText(instructions)}
    </InstructionsCellWrapper>
  );
};

type FrequencyCellProps = {
  frequency: Frequency;
};

export const FrequencyCell = ({ frequency }: FrequencyCellProps) => (
  <FrequencyCellWrapper data-test="frequency-cell">
    {FREQUENCY_LABELS[frequency]}
  </FrequencyCellWrapper>
);

type LastModifiedCellProps = {
  updatedAt?: LastModified;
  updatedBy?: User;
};

export const LastModifiedCell = ({ updatedAt, updatedBy }: LastModifiedCellProps) => {
  const updatedByEmail = updatedBy?.id;
  const updatedByFullName = updatedBy?.fullName;
  const updatedByAvatarUrl = updatedBy?.avatarUrl;

  const updatedByFullNameArray = updatedByFullName?.split(' ');
  const updatedByFirstName = updatedByFullNameArray?.[0] || '';
  const updatedByLastName = updatedByFullNameArray?.[1] || '';

  const tooltipContent = (
    <>
      {`${updatedByFirstName} ${updatedByLastName}`}
      {updatedByFirstName.length > 0 && <br />}
      {updatedByEmail}
    </>
  );

  return (
    <LastModifiedCellWrapper data-test="last-modified-cell">
      <StyledTooltip isEnabled={!!updatedBy} tooltipContent={tooltipContent}>
        {updatedBy ? (
          <Avatar
            data-test="last-modified-cell-avatar"
            firstName={
              updatedByFirstName || (updatedByEmail?.length && updatedByEmail.charAt(0)) || ''
            }
            lastName={updatedByLastName || ''}
            imgSrc={updatedByAvatarUrl || ''}
            size={AVATAR_SIZES.MEDIUM}
          />
        ) : (
          <Logo height={32} />
        )}
      </StyledTooltip>
      <LastModifiedDatesWrapper>
        {updatedAt
          ? displayDateWithTodayAndYesterdayPrefix(getDateTime(updatedAt.timestamp))
          : DISPLAY_CONTENT.contentTable.lastModifiedIterableDefaultLabel}
      </LastModifiedDatesWrapper>
    </LastModifiedCellWrapper>
  );
};
